<script setup lang="ts">
import { computed } from 'vue'
import dayjs from '@/lib/dayjs'
import { useScreen } from '@/composables/useScreen'
import DesktopListToolbar from './DesktopListToolbar.vue'
import MobileListToolbar from './MobileListToolbar.vue'
import WebinarFilters from '@/ui-kit/webinars/Filters.vue'
import type { ViewMode } from '../types'

const props = defineProps<{
  viewMode: ViewMode
  viewDate: Date
  loading?: boolean
  filters?: Record<string, unknown>
  query?: string
}>()

const emit = defineEmits<{
  (e: 'update:view-mode', value: ViewMode): void
  (e: 'update:filters', value: Record<string, unknown>): void
  (e: 'update:view-date', value: Date): void
  (e: 'update:query', value: string): void
}>()

const { isMobile } = useScreen()

const viewDateFormatted = computed(() => {
  switch (props.viewMode) {
    case 'day':
      return dayjs(props.viewDate).format(isMobile.value ? 'MMM D' : 'MMMM D')
    case 'week': {
      const obj = dayjs(props.viewDate)
      const startOfWeek = obj.startOf('week').format('D')
      const endOfWeek = obj.endOf('week').format('D')
      const month = obj.format('MMMM')
      return `${month} ${startOfWeek} - ${endOfWeek}`
    }
    case 'list':
    case 'month':
      return dayjs(props.viewDate).format(isMobile.value ? 'MMM YYYY' : 'MMMM YYYY')
    default:
      return ''
  }
})

function step(val: number) {
  const unit = props.viewMode === 'list' ? 'month' : props.viewMode
  const newDate = dayjs(props.viewDate).add(val, unit)
  emit('update:view-date', newDate.toDate())
}

function today() {
  emit('update:view-date', new Date())
}

function capitalize(value: string) {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}
</script>

<template>
  <div class="d-flex flex-column">
    <div
      v-if="isMobile && viewMode !== 'list'"
      class="webinar-calendar__view-controls"
      :disabled="loading || undefined"
    >
      <v-link :class="{ 'webinar-calendar__view-active': viewMode === 'day' }" @click="emit('update:view-mode', 'day')">
        День
      </v-link>
      <v-link
        :class="{ 'webinar-calendar__view-active': viewMode === 'week' }"
        @click="emit('update:view-mode', 'week')"
      >
        Неделя
      </v-link>
      <v-link
        :class="{ 'webinar-calendar__view-active': viewMode === 'month' }"
        @click="emit('update:view-mode', 'month')"
      >
        Месяц
      </v-link>

      <v-link @click="emit('update:view-mode', 'list')"> Список </v-link>
    </div>

    <WebinarFilters
      v-if="viewMode !== 'list'"
      :model-value="filters"
      @update:model-value="val => emit('update:filters', val)"
    ></WebinarFilters>

    <div v-if="viewMode !== 'list'" class="webinar-calendar__toolbar">
      <div class="webinar-calendar__date-controls">
        <v-button mode="outlined" @click="today">Сегодня</v-button>
      </div>

      <div class="webinar-calendar__date-display">
        <button class="webinar-calendar__toolbar-btn mr-2" @click="step(-1)">
          <v-icon>mdi-chevron-left</v-icon>
        </button>
        <button class="webinar-calendar__toolbar-btn mr-2" @click="step(1)">
          <v-icon>mdi-chevron-right</v-icon>
        </button>

        <v-calendar-menu
          ref="calendarMenu"
          pos-horizontal="center"
          :month-selector="viewMode === 'month'"
          :highlight-week="viewMode === 'week'"
          :model-value="viewDate"
          @update:model-value="(val: Date) => emit('update:view-date', val)"
        >
          <template #activator="{ onclick }">
            <div class="webinar-calendar__date-selector" @click="onclick">
              <span>{{ capitalize(viewDateFormatted) }}</span>
              <v-icon class="webinar-calendar__toolbar-btn"> mdi-chevron-down </v-icon>
            </div>
          </template>
        </v-calendar-menu>
      </div>
    </div>

    <template v-else-if="isMobile">
      <v-input
        class="mb-2"
        placeholder="Название или имя автора"
        clearable
        :model-value="query"
        @update:model-value="(v: string) => emit('update:query', v)"
      />

      <MobileListToolbar :filters="filters" @update:filters="val => emit('update:filters', val)" />
    </template>

    <DesktopListToolbar
      v-else
      :filters="filters"
      :query="query"
      @update:filters="val => emit('update:filters', val)"
      @update:query="val => emit('update:query', val)"
    />
  </div>
</template>

<style lang="scss">
.webinar-calendar {
  &__toolbar {
    display: grid;
    grid-template-columns: 1fr auto 1fr;

    @include mobile {
      padding-bottom: 16px;
    }
  }

  // Left-side date controls
  &__date-controls {
    padding: 16px 0;
    display: flex;
    align-items: center;

    // Gap between elements
    & > * + * {
      margin-left: 24px;
    }
  }

  &__toolbar-btn {
    color: $gray;
    cursor: pointer;
    transition: color 0.1s ease;
    &:hover {
      color: $darkGray;
    }
  }

  // Centre date selector
  &__date-display {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 16px;
  }

  &__date-selector {
    display: flex;
    align-items: center;

    user-select: none;
    cursor: pointer;

    @include text-title;
    font-weight: bold;
    & > * + * {
      margin-left: 12px;
    }
  }

  // Right-side view controls
  &__view-controls {
    padding: 16px 0;
    display: flex;
    align-items: center;

    font-weight: bold;

    & > * {
      flex: 1;
      justify-content: center;

      padding-bottom: 6px;
      border-bottom: 3px solid transparent;

      &:not(:first-child) {
        margin-left: 24px;
      }
    }

    &[disabled] {
      opacity: 0.5;
      pointer-events: none;
      color: $gray;
    }
  }

  &__view-active.v-link {
    color: $blue;
    border-color: $blue;
  }

  // @include mobile {
  //   &__toolbar {
  //     display: flex;
  //     flex-wrap: wrap;
  //   }

  //   &__date-controls {
  //     padding: 0;
  //   }

  //   &__view-controls {
  //     flex: 1;
  //   }
  // }
}
</style>
